import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { Sparkles, GripVertical, Plus, Info, LayoutTemplate, Lock } from 'lucide-react';
import Tooltip from '../../components/ui/Tooltip';
import PersonCell from '../../components/ui/PersonCell';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { createSurvey, addQuestion, publishSurvey, publishPublicSurvey, aiGenerateSurvey, getSurvey, updateSurvey, updateQuestion, deleteQuestion, reorderQuestions } from '../../services/surveyService';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput';
import GenericSelect from '../../components/ui/GenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import DateTimePicker from '../../components/ui/DateTimePicker';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import api from '../../services/api';
import TemplateStartDrawer from './TemplateStartDrawer';
import QuestionEditorCard from './QuestionEditorCard';
import { persistQuestionEdits, reconcileAfterPartialSave, syncCreatedQuestions } from './questionEditPersistence';
import {
  SCALE, MAX_HIGHLIGHTS, QUESTION_TYPES, QUESTION_TYPE_COLORS, typeLabelKey, tempId, newQuestion, fromServerQuestion,
  toQuestionPayload, validateQuestions, highlightCount, toggleHighlight, canUsePreviousOptions, applyPreviousOptions,
  readOptionClipboard, writeOptionClipboard, pasteOptions,
} from './questionEditorModel';

// Spec 2026-10-16 U1 — içerik tam genişlik (sayfa kenar boşluğu uygulama kabından); yapışkan kalan YALNIZ başlık + adım
// göstergesi. Anket başlığı/açıklama kartı ve sorular tek kaydırmayla birlikte kayar (iç içe kaydırma yok).
const Container = styled.div`
  width: 100%;
`;

const StickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 50;
  background: ${({ theme }) => theme.colors.bgColor};
  padding-bottom: 12px;
  margin-bottom: 12px;
`;

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StepDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  font-weight: 700;
  background: ${({ $active, $completed, theme }) =>
    $active ? theme.colors.primary.main :
    $completed ? '#16a34a' : '#E0E4E8'};
  color: ${({ $active, $completed }) => ($active || $completed) ? '#fff' : '#64748b'};
`;

const StepLine = styled.div`
  flex: 1;
  height: 2px;
  background: ${({ $completed }) => $completed ? '#16a34a' : '#E0E4E8'};
`;

const StepLabel = styled.span`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $active, theme }) => $active ? theme.colors.text.primary : '#A0A8B4'};
`;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: ${({ $color }) => $color + '15'};
  color: ${({ $color }) => $color};
`;

const Row = styled.div`
  display: flex;
  gap: ${({ $gap }) => $gap || '12px'};
  align-items: ${({ $align }) => $align || 'flex-start'};
  margin-bottom: ${({ $mb }) => $mb || '0'};
  flex-wrap: ${({ $wrap }) => $wrap || 'nowrap'};
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  width: 500px;
  max-width: 90vw;
`;

const ReorderItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 6px;
  margin-bottom: 8px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
`;

const ReorderNumber = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary.main};
  min-width: 24px;
`;

const PublishCard = styled(Card)``;

function SortableQuestion({ question, index, t }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: question.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ReorderItem ref={setNodeRef} style={style}>
      <GripVertical size={16} style={{ color: '#A0A8B4', cursor: 'grab' }} {...attributes} {...listeners} />
      <ReorderNumber>{index + 1}</ReorderNumber>
      <span style={{ flex: 1 }}>{question.text}</span>
      <TypeBadge $color={QUESTION_TYPE_COLORS[question.type]}>
        {t(typeLabelKey(question.type))}
      </TypeBadge>
    </ReorderItem>
  );
}

// Yeniden inceleme N1 — /surveys/create ve /surveys/:id/edit aynı bileşeni anahtarsız bağlar; react-router v5 rota değişince
// (sayfadaki "Şablondan Başlat" → /surveys/{id}/edit) bileşeni yeniden bağlamaz ve eski taslak kimliği state'te kalırdı
// (Yayınla eski taslağı ya da null'u yayınlardı). Rota kimliğiyle anahtarlanınca her kimlikte state sıfırdan kurulur.
export default function SurveyCreatePage() {
  const { id } = useParams();
  return <SurveyCreatePageInner key={id || 'new'} />;
}

function SurveyCreatePageInner() {
  const { t } = useTranslation();
  const history = useHistory();
  const { id: editId } = useParams();
  const { isSuperAdmin, hasModule } = useAuth();
  // M6 — AI üretici AI+ ister (superAdmin hasModule'de her zaman true). AI+ yoksa düğme kilitli görünür, istek gitmez.
  const aiAllowed = hasModule('AI');

  // I6 — düzenleme kipi (şablondan başlat dahil) de soru düzenleyicide açılır: şablon ipucunun istediği seçenek
  // düzenlemesi ilk ekranda görünür.
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [surveyId, setSurveyId] = useState(editId || null);
  const [publicMaxResponses, setPublicMaxResponses] = useState('');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setQuestions((prev) => {
      const oldIndex = prev.findIndex((q) => q.id === active.id);
      const newIndex = prev.findIndex((q) => q.id === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  // Step 1
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  // Düzenleme kipinde sunucudaki soru listesi — kayıtta fark bundan çıkarılır (final inceleme I6).
  const [originalQuestions, setOriginalQuestions] = useState([]);
  // Spec S1 — "Ölçek Sorusu" tip seçicide ilk sırada ve varsayılan.
  const [newQuestionType, setNewQuestionType] = useState(SCALE);
  // Spec S3 — oturum boyu seçenek panosu (modül belleği; başka ankette kopyalanan da yapıştırılır).
  const [clipboard, setClipboard] = useState(() => readOptionClipboard());
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  // Yeniden inceleme N3 — create yanıtında questionIds yoksa (yeni SPA eski API'ye denk geldi) ve taslağın soruları da
  // okunamadıysa, create'e gönderilen geçici kimlikli liste. Sunucu kimlikleri okunana kadar fark kaydı yapılmaz; aksi
  // hâlde bütün sorular taslağa ikinci kez eklenirdi.
  const unsyncedCreate = useRef(null);

  // Step 3
  const [deadline, setDeadline] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [anonymityThreshold, setAnonymityThreshold] = useState('5');
  const [targetEmployees, setTargetEmployees] = useState([]);
  const [targetDepartments, setTargetDepartments] = useState([]);
  const [targetLocations, setTargetLocations] = useState([]);
  const [targetUserGroups, setTargetUserGroups] = useState([]);

  // Edit mode: load existing survey + questions
  useEffect(() => {
    if (!editId) return;
    getSurvey(editId).then((res) => {
      const s = res.data?.data;
      if (!s) { history.replace('/surveys'); return; }
      if (s.status !== 'draft') { history.replace(`/surveys/${editId}`); return; }
      setTitle(s.title || '');
      setDescription(s.description || '');
      // Ölçek sorusu (singleChoice + puanlar tam 1..5) yeniden tanınır; olumsuz ifade ve öne çıkar GetById'dan gelir.
      const mapped = (s.questions || []).map(fromServerQuestion);
      setQuestions(mapped);
      setOriginalQuestions(mapped);
    }).catch(() => history.replace('/surveys'));
  }, [editId, history]);

  const typeOptions = useMemo(() =>
    QUESTION_TYPES.map((qt) => ({ id: qt.id, name: t(qt.name) })),
    [t]
  );

  const addQuestionHandler = useCallback(() => {
    setQuestions((prev) => [...prev, newQuestion(newQuestionType, t)]);
  }, [newQuestionType, t]);

  const updateQuestionField = useCallback((qId, field, value) => {
    setQuestions((prev) => prev.map((q) => q.id === qId ? { ...q, [field]: value } : q));
  }, []);

  const removeQuestion = useCallback((qId) => {
    setQuestions((prev) => prev.filter((q) => q.id !== qId));
  }, []);

  const addOption = useCallback((qId) => {
    setQuestions((prev) => prev.map((q) => (q.id !== qId ? q
      : { ...q, options: [...q.options, { id: tempId(), label: '', score: null }] })));
  }, []);

  const updateOptionLabel = useCallback((qId, optId, label) => {
    setQuestions((prev) => prev.map((q) => (q.id !== qId ? q
      : { ...q, options: q.options.map((o) => (o.id === optId ? { ...o, label } : o)) })));
  }, []);

  const removeOption = useCallback((qId, optId) => {
    setQuestions((prev) => prev.map((q) => (q.id !== qId ? q
      : { ...q, options: q.options.filter((o) => o.id !== optId) })));
  }, []);

  const copyOptions = useCallback((q) => setClipboard(writeOptionClipboard(q)), []);

  const pasteInto = useCallback((qId) => {
    setQuestions((prev) => prev.map((q) => (q.id === qId ? pasteOptions(q, readOptionClipboard()) : q)));
  }, []);

  const fillFromPrevious = useCallback((index) => {
    setQuestions((prev) => applyPreviousOptions(prev, index));
  }, []);

  const toggleQuestionHighlight = useCallback((qId) => {
    setQuestions((prev) => toggleHighlight(prev, qId));
  }, []);

  const handleAiGenerate = useCallback(async () => {
    if (!aiAllowed || !aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await aiGenerateSurvey({ prompt: aiPrompt });
      const generated = res.data?.data;
      if (generated) {
        if (generated.title) setTitle(generated.title);
        if (generated.description) setDescription(generated.description);
        // Spec 2026-10-16 S1/S4: Likert (singleChoice + 1–5) Ölçek Sorusu olarak gelir; boyut/kod GÖNDERİLMEZ — yapı yayında
        // bölüm başlıklarından kurulur (netleşen 12).
        if (Array.isArray(generated.questions)) {
          setQuestions((prev) => [...prev, ...generated.questions.map(fromServerQuestion)]);
        }
      }
      setAiModalOpen(false);
      setAiPrompt('');
    } catch {
      // error handled by interceptor
    } finally {
      setAiLoading(false);
    }
  }, [aiPrompt, aiAllowed]);

  const step1Valid = validateQuestions(title, questions);
  const highlightsFull = highlightCount(questions) >= MAX_HIGHLIGHTS;

  const handleStep1Next = useCallback(async () => {
    if (!validateQuestions(title, questions)) return;
    setSaving(true);
    // Son inceleme I4 — taslak bir kez oluşturulur: adım 2'den geri dönülüp yeniden İleri denince (yeni ankette de)
    // oluşturulan taslağa fark kaydı yapılır; ikinci taslak açılmaz.
    const targetId = editId || surveyId;
    let original = originalQuestions;
    let current = questions;
    try {
      const payload = {
        title,
        description,
        questions: current.map(toQuestionPayload),
      };
      if (targetId) {
        if (unsyncedCreate.current) {
          // N3 — önce taslağın sunucu kimlikleri okunur; okunamazsa hiçbir şey yazılmaz, adım 1'de kalınır.
          const synced = await syncCreatedQuestions(targetId, unsyncedCreate.current, current, getSurvey, fromServerQuestion);
          if (!synced) return;
          unsyncedCreate.current = null;
          original = synced.original;
          current = synced.questions;
          setOriginalQuestions(original);
          setQuestions(current);
        }
        // Update yalnız başlık/açıklamayı yazar; soru farkı (değişen/eklenen/silinen + sıra) ayrıca yazılır.
        await updateSurvey(targetId, payload);
        const ids = await persistQuestionEdits(targetId, original, current, toQuestionPayload,
          { addQuestion, updateQuestion, deleteQuestion, reorderQuestions });
        const saved = current.map((q, i) => ({ ...q, id: ids[i] }));
        setQuestions(saved);
        setOriginalQuestions(saved);
        setStep(2);
      } else {
        const surveyRes = await createSurvey(payload);
        const created = surveyRes.data?.data;
        const newId = created?.id;
        if (!newId) return;
        setSurveyId(newId);
        // Geçici kimlikler sunucu kimlikleriyle değişir (Create istek sırasıyla döner) — sonraki İleri fark kaydıdır.
        const ids = Array.isArray(created.questionIds) && created.questionIds.length === current.length
          ? created.questionIds : null;
        if (ids) {
          const saved = current.map((q, i) => ({ ...q, id: ids[i] }));
          setQuestions(saved);
          setOriginalQuestions(saved);
          setStep(2);
          return;
        }
        // Yeniden inceleme N3 — yanıtta questionIds yok ya da sayısı tutmuyor: taslağın soruları getSurvey ile okunur
        // (GET reddetmez, sinyal res.data.status). Okunamazsa adım 1'de kalınır; sonraki İleri önce yeniden okur.
        const synced = await syncCreatedQuestions(newId, current, current, getSurvey, fromServerQuestion);
        if (!synced) {
          unsyncedCreate.current = current;
          return;
        }
        setQuestions(synced.questions);
        setOriginalQuestions(synced.original);
        setStep(2);
      }
    } catch (err) {
      // Hata toast'u interceptor'da. Soru kaydı yarıda kaldıysa (yeniden inceleme N3) yüklü liste sunucu durumuna çekilir
      // ve eklenen soruların geçici kimlikleri yenileriyle değişir — yeniden denemede silinen soru tekrar silinmez (404),
      // eklenen tekrar eklenmez.
      if (targetId && err?.applied) {
        const { idMap, original: next } = reconcileAfterPartialSave(original, current, err.applied);
        setOriginalQuestions(next);
        if (idMap.size > 0) setQuestions((prev) => prev.map((q) => (idMap.has(q.id) ? { ...q, id: idMap.get(q.id) } : q)));
      }
    } finally {
      setSaving(false);
    }
  }, [title, description, questions, originalQuestions, editId, surveyId]);

  // Son inceleme I5 — sıralama ekranındaki sıra sunucuya yazılır (rapor kodları, gösterge ve katılımcı ekranı bu sırayı
  // kullanır). Adım 1 her İleri'de sunucu kimliklerini yazdığı için burada bütün kimlikler sunucu kimliğidir.
  const handleStep2Next = useCallback(async () => {
    const targetId = editId || surveyId;
    if (!targetId) return;
    setSaving(true);
    try {
      await reorderQuestions(targetId, { items: questions.map((q, i) => ({ id: q.id, orderIndex: i })) });
      setOriginalQuestions(questions);
      setStep(3);
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [editId, surveyId, questions]);

  const handlePublish = useCallback(async () => {
    const targetId = editId || surveyId;
    if (!deadline || !targetId) return;
    setSaving(true);
    try {
      await publishSurvey(targetId, {
        deadline,
        isAnonymous,
        anonymityThreshold: isAnonymous ? Number(anonymityThreshold) || 5 : undefined,
        employeeIds: targetEmployees.length > 0 ? targetEmployees : undefined,
        departmentIds: targetDepartments.length > 0 ? targetDepartments : undefined,
        locationIds: targetLocations.length > 0 ? targetLocations : undefined,
        userGroupIds: targetUserGroups.length > 0 ? targetUserGroups : undefined,
      });
      history.push('/surveys');
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [editId, surveyId, deadline, isAnonymous, anonymityThreshold, targetEmployees, targetDepartments, targetLocations, targetUserGroups, history]);

  const handlePublishPublic = useCallback(async () => {
    const targetId = editId || surveyId;
    if (!deadline || !targetId) return;
    setSaving(true);
    try {
      await publishPublicSurvey(targetId, {
        expiresAt: deadline,
        maxResponses: publicMaxResponses ? parseInt(publicMaxResponses) : null,
      });
      history.push('/public-survey-dashboard');
    } catch {
      // error handled by interceptor
    } finally {
      setSaving(false);
    }
  }, [editId, surveyId, deadline, publicMaxResponses, history]);

  const searchEmployees = useCallback(async (term) => {
    const params = term ? { 'firstName[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    return (res.data?.data?.data || []).map((e) => ({
      id: e.id,
      name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
      profilePicture: e.profilePicture,
    }));
  }, []);

  const searchDepartments = useCallback(async (term) => {
    const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/departments', { params });
    return (res.data?.data?.data || []).map((d) => ({ id: d.id, name: d.name }));
  }, []);

  const searchLocations = useCallback(async (term) => {
    const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/locations', { params });
    return (res.data?.data?.data || []).map((l) => ({ id: l.id, name: l.name }));
  }, []);

  const searchUserGroups = useCallback(async (term) => {
    const params = term ? { 'name[like]': term, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/user-groups', { params });
    return (res.data?.data?.data || []).map((g) => ({ id: g.id, name: g.name }));
  }, []);

  return (
    <Container>
      <StickyHeader>
        <h2 style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 20, fontWeight: 700, margin: '0 0 12px', color: '#004242' }}>
          {t('survey.create')}
        </h2>

        <StepIndicator>
          <StepDot $active={step === 1} $completed={step > 1}>1</StepDot>
          <StepLabel $active={step === 1}>{t('survey.step1')}</StepLabel>
          <StepLine $completed={step > 1} />
          <StepDot $active={step === 2} $completed={step > 2}>2</StepDot>
          <StepLabel $active={step === 2}>{t('survey.step2')}</StepLabel>
          <StepLine $completed={step > 2} />
          <StepDot $active={step === 3}>3</StepDot>
          <StepLabel $active={step === 3}>{t('survey.step3')}</StepLabel>
        </StepIndicator>
      </StickyHeader>

      {/* Step 1 — başlık kartı ve sorular içerikle birlikte kayar (U1). "İleri" alttaki Footer'da (netleşen 18). */}
      {step === 1 && (
        <>
          <Card>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <TextInput
                  label={t('survey.surveyTitle')}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                />
              </div>
              {/* Spec S6: şablon AI değildir — yalnız Anketler modülü; yeni ankette görünür. */}
              {!editId && (
                <Button variant="ghost" onClick={() => setTemplateOpen(true)} style={{ flexShrink: 0, height: 48 }}>
                  <LayoutTemplate size={16} /> {t('survey.template.start')}
                </Button>
              )}
              {aiAllowed ? (
                <Button variant="ghost" onClick={() => setAiModalOpen(true)} style={{ flexShrink: 0, height: 48 }}>
                  <Sparkles size={16} /> {t('survey.aiGenerate')}
                </Button>
              ) : (
                // M6 — AI+ olmayan müşteride kilitli: tıklanmaz, istek gitmez; ipucu "AI+ İle Açılır".
                <Tooltip text={t('survey.report.aiPlus.title')} position="bottom">
                  <span data-testid="ai-generate-locked" style={{ display: 'inline-flex' }}>
                    <Button variant="ghost" disabled style={{ flexShrink: 0, height: 48 }}>
                      <Lock size={16} /> {t('survey.aiGenerate')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </div>
            <TextInput
              label={t('survey.description')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              type="textarea"
              rows={2}
            />
          </Card>

          {questions.map((q, idx) => (
            <QuestionEditorCard
              key={q.id}
              question={q}
              index={idx}
              questions={questions}
              clipboard={clipboard}
              canUsePrevious={canUsePreviousOptions(questions, idx)}
              highlightLocked={highlightsFull}
              onChange={(field, value) => updateQuestionField(q.id, field, value)}
              onOptionLabelChange={(optId, label) => updateOptionLabel(q.id, optId, label)}
              onAddOption={() => addOption(q.id)}
              onRemoveOption={(optId) => removeOption(q.id, optId)}
              onRemove={() => removeQuestion(q.id)}
              onToggleHighlight={() => toggleQuestionHighlight(q.id)}
              onCopyOptions={() => copyOptions(q)}
              onPasteOptions={() => pasteInto(q.id)}
              onUsePreviousOptions={() => fillFromPrevious(idx)}
            />
          ))}

          <Card>
            <Row $align="center" $gap="12px">
              <div style={{ width: 220 }}>
                <GenericSelect
                  label={t('survey.questionType')}
                  value={newQuestionType}
                  onChange={(val) => setNewQuestionType(val)}
                  options={typeOptions}
                  idKey="id"
                  labelKey="name"
                />
              </div>
              <Button variant="outline" onClick={addQuestionHandler}>
                <Plus size={16} /> {t('survey.addQuestion')}
              </Button>
            </Row>
          </Card>

          <Footer>
            <span />
            <Button onClick={handleStep1Next} disabled={!step1Valid || saving}>
              {saving ? '...' : t('survey.next')}
            </Button>
          </Footer>

          {aiModalOpen && aiAllowed && (
            <ModalOverlay onClick={() => !aiLoading && setAiModalOpen(false)}>
              <ModalContent onClick={(e) => e.stopPropagation()}>
                <h3 style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 16, fontWeight: 700, marginBottom: 16, color: '#004242' }}>
                  {t('survey.aiGenerate')}
                </h3>
                <TextInput
                  label={t('survey.aiGeneratePrompt')}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  textarea
                />
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <Button variant="ghost" onClick={() => setAiModalOpen(false)} disabled={aiLoading}>
                    {t('common.cancel')}
                  </Button>
                  <Button onClick={handleAiGenerate} disabled={!aiPrompt.trim() || aiLoading}>
                    <Sparkles size={14} /> {aiLoading ? '...' : t('survey.aiGenerateButton')}
                  </Button>
                </div>
              </ModalContent>
            </ModalOverlay>
          )}
        </>
      )}

      {/* Step 2 — Reorder */}
      {step === 2 && (
        <>
          <Card>
            <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 14, fontWeight: 600, color: '#004242', marginBottom: 16 }}>
              {t('survey.questions')}
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={questions.map(q => q.id)} strategy={verticalListSortingStrategy}>
                {questions.map((q, idx) => (
                  <SortableQuestion key={q.id} question={q} index={idx} t={t} />
                ))}
              </SortableContext>
            </DndContext>
          </Card>

          <Footer>
            <Button variant="ghost" onClick={() => setStep(1)}>
              {t('survey.previous')}
            </Button>
            {/* I5 — "Atla" kaldırıldı: sıra her zaman İleri ile kaydedilir (tek yol). */}
            <Button onClick={handleStep2Next} disabled={saving}>
              {saving ? '...' : t('survey.next')}
            </Button>
          </Footer>
        </>
      )}

      {/* Step 3 — Publish */}
      {step === 3 && isSuperAdmin && (
        <>
          <PublishCard>
            <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 16, fontWeight: 700, color: '#004242', marginBottom: 20 }}>
              {t('survey.publishPublic')}
            </div>

            <div style={{ marginBottom: 16 }}>
              <DateTimePicker
                label={t('survey.expiresAt')}
                value={deadline}
                onChange={setDeadline}
                required
              />
            </div>

            <div style={{ marginBottom: 16, maxWidth: 300 }}>
              <TextInput
                label={t('survey.maxResponses')}
                value={publicMaxResponses}
                onChange={(e) => setPublicMaxResponses(e.target.value)}
                type="number"
                placeholder={t('survey.maxResponsesHint')}
              />
            </div>
          </PublishCard>

          <Footer>
            <Button variant="ghost" onClick={() => setStep(2)}>
              {t('survey.previous')}
            </Button>
            <Button onClick={handlePublishPublic} disabled={!deadline || saving}>
              {saving ? '...' : t('survey.publishPublic')}
            </Button>
          </Footer>
        </>
      )}

      {step === 3 && !isSuperAdmin && (
        <>
          <PublishCard>
            <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 16, fontWeight: 700, color: '#004242', marginBottom: 20 }}>
              {t('survey.publishTitle')}
            </div>

            <div style={{ marginBottom: 16 }}>
              <DateTimePicker
                label={t('survey.deadline')}
                value={deadline}
                onChange={setDeadline}
                required
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <CheckboxInput
                label={t('survey.isAnonymous')}
                checked={isAnonymous}
                onChange={setIsAnonymous}
              />
            </div>

            {isAnonymous && (
              <div style={{ marginBottom: 16, maxWidth: 250, display: 'flex', alignItems: 'center', gap: 8 }}>
                <TextInput
                  label={t('survey.anonymityThreshold')}
                  value={anonymityThreshold}
                  onChange={(e) => setAnonymityThreshold(e.target.value)}
                  type="number"
                />
                <Tooltip text={t('common.anonymityThresholdInfo')} position="right">
                  <Info size={16} style={{ color: '#A0A8B4', cursor: 'pointer', flexShrink: 0 }} />
                </Tooltip>
              </div>
            )}

            <div style={{ fontFamily: "'Nunito Sans', sans-serif", fontSize: 13, fontWeight: 600, color: '#A0A8B4', marginBottom: 12, marginTop: 24 }}>
              {t('common.targets') || 'targets'}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <AsyncGenericSelect
                label={t('employee.title')}
                value={targetEmployees}
                onChange={setTargetEmployees}
                onSearch={searchEmployees}
                renderOption={(opt) => <PersonCell name={opt.name} profilePicture={opt.profilePicture} />}
                isMulti
              />
              <AsyncGenericSelect
                label={t('department.title')}
                value={targetDepartments}
                onChange={setTargetDepartments}
                onSearch={searchDepartments}
                isMulti
              />
              <AsyncGenericSelect
                label={t('location.title')}
                value={targetLocations}
                onChange={setTargetLocations}
                onSearch={searchLocations}
                isMulti
              />
              <AsyncGenericSelect
                label={t('userGroup.title')}
                value={targetUserGroups}
                onChange={setTargetUserGroups}
                onSearch={searchUserGroups}
                isMulti
              />
            </div>
          </PublishCard>

          <Footer>
            <Button variant="ghost" onClick={() => setStep(2)}>
              {t('survey.previous')}
            </Button>
            <Button onClick={handlePublish} disabled={!deadline || saving}>
              {saving ? '...' : t('survey.publish')}
            </Button>
          </Footer>
        </>
      )}
      <TemplateStartDrawer open={templateOpen} onClose={() => setTemplateOpen(false)} />
    </Container>
  );
}
